import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { PromptInput } from '../components/PromptInput'
import { ExamplePrompts } from '../components/ExamplePrompts'
import { PublicNavBar } from '../components/PublicNavBar'
import { PublicFooter } from '../components/PublicFooter'
import { FaqSection } from '../components/FaqSection'
import { BuildView } from '../components/BuildView'
import { LoopDiagram } from '../components/LoopDiagram'
import { NotifyEmailCapture } from '../components/NotifyEmailCapture'
import PackageStrip from '../components/PackageStrip'
import { UpgradePrompt } from '../components/UpgradePrompt'
import { WizardProgress } from '../components/WizardProgress'
import { useBillingError } from '../hooks/useBillingError'
import { wizardCreate, getWizardBuild, isAuthenticated } from '../api/client'
import { usePageMeta } from '../hooks/usePageMeta'
import { pageMeta } from '../seo/pageMeta'

type Phase = 'landing' | 'creating' | 'building'

const DEMO_PROMPT = 'Build a React dashboard with a sidebar navigation, a data table showing user accounts, and a chart widget for monthly signups'

export default function Home() {
  usePageMeta(pageMeta('/'))

  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [phase, setPhase] = useState<Phase>('landing')
  const [projectId, setProjectId] = useState<string | null>(null)
  const [agentId, setAgentId] = useState<string | null>(null)
  const [projectName, setProjectName] = useState('')
  const [projectType, setProjectType] = useState('')
  const [projectLanguage, setProjectLanguage] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [prompt, setPrompt] = useState('')
  const [resumeExpired, setResumeExpired] = useState(false)
  const { billingError, handleError: handleBillingError, dismissBilling } = useBillingError()
  const autoSubmitted = useRef(false)
  const autoResumed = useRef(false)

  // Auto-submit if ?prompt= is in the URL (e.g., from Estimator CTA)
  useEffect(() => {
    const prefill = searchParams.get('prompt')
    if (prefill && !autoSubmitted.current) {
      autoSubmitted.current = true
      setSearchParams({}, { replace: true }) // Clean URL
      handleSubmit(prefill)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Resume link: ?build={projectID} (from the "build is ready" email) re-opens
  // an anonymous build in the building view. Expired builds (7-day cleanup)
  // fall back to the landing page with a friendly note.
  useEffect(() => {
    const buildId = searchParams.get('build')
    if (!buildId || autoResumed.current) return
    autoResumed.current = true
    setSearchParams({}, { replace: true }) // Clean URL
    getWizardBuild(buildId)
      .then((info) => {
        setProjectId(info.project_id)
        setProjectName(info.project_name)
        setProjectType(info.type)
        setProjectLanguage(info.language)
        setAgentId(null)
        setPhase('building')
      })
      .catch(() => setResumeExpired(true))
  }, [searchParams, setSearchParams])

  const handleSubmit = async (text: string) => {
    setPrompt(text)
    setPhase('creating')
    setError(null)
    setResumeExpired(false)

    try {
      const result = await wizardCreate({ prompt: text, mode: 'basic' })
      setProjectId(result.project_id)
      setAgentId(result.agent_id)
      setProjectName(result.project_name)
      setProjectType(result.type)
      setProjectLanguage(result.language)
      setPhase('building')
    } catch (err) {
      if (!handleBillingError(err)) {
        const msg = err instanceof Error ? err.message : 'Something went wrong. Please try again.'
        if (msg.includes('template') || msg.includes('503')) {
          setError('Project creation is temporarily unavailable — no templates configured on the server.')
        } else {
          setError(msg)
        }
      }
      setPhase('landing')
    }
  }

  return (
    <div className="min-h-screen text-gray-900 dark:text-gray-100">
      <PublicNavBar />

      {/* Landing phase */}
      {phase === 'landing' && (
        <div>
          {/* Hero section */}
          <section className="flex flex-col items-center justify-center px-4 pt-16 sm:pt-24 pb-16">
            <div className="text-center mb-8 sm:mb-10 max-w-3xl">
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold mb-5 leading-tight">
                <span className="bg-gradient-to-r from-gray-900 via-gray-700 to-gray-500 dark:from-white dark:via-gray-200 dark:to-gray-400 bg-clip-text text-transparent">
                  Describe a feature.
                </span>
                <br />
                <span className="bg-gradient-to-r from-primary-600 to-orange-500 bg-clip-text text-transparent">
                  Watch your AI team ship it.
                </span>
              </h1>
              <p className="text-base sm:text-lg text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
                Multiple agents work in parallel on isolated branches. Code reviewed automatically for architecture,
                security, and style. Ready to merge in minutes, not days.
              </p>
            </div>

            <PromptInput onSubmit={handleSubmit} placeholder="Describe what you want built..." />
            <ExamplePrompts onSelect={handleSubmit} />

            {billingError && (
              <div className="mt-4 max-w-2xl w-full">
                <UpgradePrompt message={billingError.message} code={billingError.code} limitType={billingError.limitType} onDismiss={dismissBilling} />
              </div>
            )}

            {error && !billingError && (
              <div className="mt-4 px-4 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 rounded-lg text-sm">
                {error}
              </div>
            )}

            {resumeExpired && !error && !billingError && (
              <div className="mt-4 px-4 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400 rounded-lg text-sm">
                That build has expired &mdash; anonymous builds are kept for 7 days. Describe your idea above to start a fresh one.
              </div>
            )}

            <div className="mt-6 flex flex-col items-center gap-3">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => handleSubmit(DEMO_PROMPT)}
                  className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-500 transition-colors font-medium"
                >
                  Or watch a live demo &rarr;
                </button>
                <span className="text-gray-300 dark:text-gray-600">|</span>
                <Link
                  to="/estimate"
                  className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors font-medium"
                >
                  Estimate an idea &rarr;
                </Link>
                <span className="text-gray-300 dark:text-gray-600">|</span>
                <button
                  onClick={() => isAuthenticated() ? navigate('/projects/import') : navigate('/register?next=import-project')}
                  className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors font-medium"
                >
                  Connect a Repo &rarr;
                </button>
              </div>
              <p className="text-xs text-gray-400 dark:text-gray-500">No signup required — try it right now</p>
            </div>
          </section>

          {/* How it works */}
          <section className="py-16 sm:py-24 px-4 bg-gray-50 dark:bg-gray-800/50">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl sm:text-3xl font-bold text-center mb-3">
                How it works
              </h2>
              <p className="text-center text-gray-500 dark:text-gray-400 mb-12 max-w-lg mx-auto">
                One prompt triggers an entire engineering workflow — automatically.
              </p>
              <LoopDiagram />
              <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-10 max-w-2xl mx-auto">
                Describe a feature in plain English and Factory Nexus decomposes it into independent tasks, runs{' '}
                <Link to="/products" className="text-primary-600 dark:text-primary-400 hover:text-primary-500">
                  parallel AI coding agents
                </Link>{' '}
                on isolated git branches, gates every branch behind{' '}
                <Link to="/products" className="text-primary-600 dark:text-primary-400 hover:text-primary-500">
                  AI code review
                </Link>{' '}
                for architecture, security, and style, then merges what passes. Not sure how big your idea is? Get a
                free{' '}
                <Link to="/estimate" className="text-primary-600 dark:text-primary-400 hover:text-primary-500">
                  AI software project estimate
                </Link>{' '}
                first.
              </p>
            </div>
          </section>

          {/* Why Factory Nexus */}
          <section className="py-16 sm:py-24 px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-2xl sm:text-3xl font-bold text-center mb-12">
                Not another AI autocomplete
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center p-6">
                  <div className="w-12 h-12 mx-auto mb-4 bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Parallel agents</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Up to 5 agents work simultaneously on isolated git branches.
                    One feature decomposed into parallel tasks — shipped in minutes.
                  </p>
                </div>

                <div className="text-center p-6">
                  <div className="w-12 h-12 mx-auto mb-4 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold mb-2">AI code review</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Every PR reviewed for architecture, security, and style by 3
                    concurrent AI reviewers. Failed? Agents auto-retry with feedback.
                  </p>
                </div>

                <div className="text-center p-6">
                  <div className="w-12 h-12 mx-auto mb-4 bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 8.689c0-.864.933-1.405 1.683-.977l7.108 4.062a1.125 1.125 0 010 1.953l-7.108 4.062A1.125 1.125 0 013 16.811V8.69zM12.75 8.689c0-.864.933-1.405 1.683-.977l7.108 4.062a1.125 1.125 0 010 1.953l-7.108 4.062a1.125 1.125 0 01-1.683-.977V8.69z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Ready to merge</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Reviewed, tested, and merged to your integration branch.
                    You review the final result, not every line of code.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Package Strip */}
          <PackageStrip />

          {/* Simple pricing */}
          <section className="py-16 sm:py-24 px-4 bg-gray-50 dark:bg-gray-800/50">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl sm:text-3xl font-bold text-center mb-3">
                Simple pricing
              </h2>
              <p className="text-center text-gray-500 dark:text-gray-400 mb-10">
                Start free. Upgrade when your AI team needs to scale.
              </p>
              <div className="grid sm:grid-cols-2 gap-6">
                {/* Free */}
                <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
                  <h3 className="text-lg font-semibold mb-1">Free</h3>
                  <p className="text-3xl font-bold mb-4">$0<span className="text-sm font-normal text-gray-400">/mo</span></p>
                  <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400 mb-6">
                    <li className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                      5 projects
                    </li>
                    <li className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                      1 concurrent agent
                    </li>
                    <li className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                      31 project templates
                    </li>
                    <li className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                      GitHub import
                    </li>
                  </ul>
                  <Link
                    to="/register"
                    className="block text-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:border-gray-400 transition-colors text-sm"
                  >
                    Get started free
                  </Link>
                </div>

                {/* Pro */}
                <div className="bg-white dark:bg-gray-900 rounded-2xl border-2 border-primary-500 p-6 relative">
                  <div className="absolute -top-3 left-6 px-3 py-0.5 bg-primary-600 text-white text-xs font-medium rounded-full">
                    Most popular
                  </div>
                  <h3 className="text-lg font-semibold mb-1">Pro</h3>
                  <p className="text-3xl font-bold mb-4">$19<span className="text-sm font-normal text-gray-400">/mo</span></p>
                  <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400 mb-6">
                    <li className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                      25 projects
                    </li>
                    <li className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                      <strong>5 parallel agents</strong>
                    </li>
                    <li className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                      <strong>AI code review</strong> (3 concurrent checks)
                    </li>
                    <li className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                      <strong>Orchestration</strong> (AI task decomposition)
                    </li>
                    <li className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                      Docker builds & tests
                    </li>
                  </ul>
                  <Link
                    to="/register"
                    className="block text-center px-4 py-2 bg-gradient-to-r from-primary-600 to-orange-500 text-white font-medium rounded-lg hover:from-primary-500 hover:to-orange-400 transition-all text-sm shadow-lg shadow-orange-500/20"
                  >
                    Start building
                  </Link>
                </div>
              </div>
              <p className="text-center text-xs text-gray-400 dark:text-gray-500 mt-4">
                Teams plan available at $49/seat/mo.{' '}
                <a href="mailto:support@notify.factory-nexus.ai" className="text-primary-500 hover:text-primary-400">Contact us</a>
              </p>
              <p className="text-center mt-3">
                <Link to="/pricing" className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-500 transition-colors">
                  See full pricing &rarr;
                </Link>
              </p>
            </div>
          </section>

          <FaqSection path="/" heading="Parallel AI coding agents: common questions" />

          {/* Bottom CTA */}
          <section className="py-16 sm:py-24 px-4">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                Stop coding alone
              </h2>
              <p className="text-gray-500 dark:text-gray-400 mb-8">
                Describe what you need. Your AI team handles the rest.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <button
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="px-6 py-3 bg-gradient-to-r from-primary-600 to-orange-500 text-white font-medium rounded-lg hover:from-primary-500 hover:to-orange-400 transition-all shadow-lg shadow-orange-500/20"
                >
                  Try it now
                </button>
                <Link
                  to="/register"
                  className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 font-medium rounded-lg hover:border-gray-400 hover:text-gray-900 dark:hover:text-white transition-all"
                >
                  Get started free
                </Link>
              </div>
            </div>
          </section>

          <PublicFooter />
        </div>
      )}

      {/* Creating phase — staged progress while the wizard call is in flight */}
      {phase === 'creating' && (
        <div className="flex flex-col items-center justify-center px-4" style={{ minHeight: 'calc(100vh - 64px)' }}>
          <WizardProgress prompt={prompt} />
        </div>
      )}

      {/* Building phase — side-by-side chat + preview */}
      {phase === 'building' && projectId && (
        <div style={{ height: 'calc(100vh - 64px)' }}>
          <BuildView
            projectId={projectId}
            agentId={agentId}
            projectName={projectName}
            projectType={projectType}
            projectLanguage={projectLanguage}
            showSignupCTA
          />
        </div>
      )}

      {/* Email-me-when-done for anonymous builds — stays mounted across the
          creating → building transition so a pending submit survives it */}
      {phase !== 'landing' && !isAuthenticated() && <NotifyEmailCapture projectId={projectId} />}
    </div>
  )
}
